import { createSelector } from '@reduxjs/toolkit';

import { RootState } from 'types';
import {
  CartAddon,
  CartBundle,
  CartSeat,
  CartTicket,
  CartTopup,
} from 'types/CartItem';
import { isAddon, isBundle, isTicket, isTopup } from 'types/SelectedItem';
import { getPriceWithoutTax, getTaxPrice } from 'utils/tickets';
import { initialState } from '.';
import { getTicketTypes, hasUnbundledAddons } from 'utils/bundles';
import { hasUnbundledAddons as ticketHasUnbundledAddons } from 'utils/tickets';

const selectSlice = (state: RootState) => state.cart || initialState;

export const selectCartData = createSelector([selectSlice], state => state);

export const selectLoadingStateFromCart = createSelector(
  [selectSlice],
  state => state.loading,
);

export const selectEventFromCart = createSelector(
  [selectSlice],
  state => state.selectedEvent,
);

export const selectCartUUID = createSelector(
  [selectSlice],
  state => state.uuid,
);

export const selectVoucherDataFromCart = createSelector(
  [selectSlice],
  state => state.voucher,
);

export const selectItemsFromCart = createSelector(
  [selectSlice],
  state => state.selectedItems,
);

export const selectSelectedTicketsFromCart = createSelector(
  [selectSlice],
  state =>
    state.selectedItems.filter((item): item is CartTicket | CartSeat =>
      isTicket(item),
    ),
);

export const selectSelectedBundlesFromCart = createSelector(
  [selectSlice],
  state =>
    state.selectedItems.filter((item): item is CartBundle => isBundle(item)),
);

export const selectMergedSelectedAddonsFromCart = createSelector(
  [selectSlice],
  state => {
    const mergedAddons: CartAddon[] = [];
    const { selectedItems } = state;

    for (let i = 0; i < selectedItems.length; i++) {
      const item = selectedItems[i];
      if (isAddon(item)) {
        const indexOfCurrentAddonInMergedAddons = mergedAddons.findIndex(
          addon =>
            addon.addonId === item.addonId && addon.ticketId === item.ticketId,
        );

        if (indexOfCurrentAddonInMergedAddons >= 0) {
          mergedAddons[indexOfCurrentAddonInMergedAddons].quantity +=
            selectedItems[i].quantity;
        } else {
          mergedAddons.push({ ...item });
        }
      }
    }

    return mergedAddons;
  },
);

// search for the first cart ticket with paid addon
export const selectWhichChecksIfThereAreAddonsOnTheCartEvent = createSelector(
  [selectSlice],
  state => {
    for (const selectedItem of state.selectedItems) {
      if (isTicket(selectedItem)) {
        const ticketTypeHasPaidAddon = state.selectedEvent.tickets.find(
          ticket =>
            ticket.id === selectedItem.ticketId &&
            ticket.ticketaddons.find(addon => !addon.is_bundled),
        );
        if (ticketTypeHasPaidAddon) {
          return true;
        }
      }
      if (isBundle(selectedItem)) {
        const bundle = state.selectedEvent.ticketTypeBundles?.find(
          bundle => bundle.id === selectedItem.id,
        );
        const ticketTypes = getTicketTypes(
          bundle!.items,
          state.selectedEvent.tickets,
        );
        if (hasUnbundledAddons(ticketTypes)) {
          return true;
        }
      }
    }
    return false;
  },
);

export const selectTopUpsFromCart = createSelector([selectSlice], state =>
  state.selectedItems.filter((item): item is CartTopup => isTopup(item)),
);

export const selectTotalPriceOfTopUpsFromCart = createSelector(
  [selectSlice],
  state => {
    const {
      selectedItems,
      selectedEvent: { tax_rate, topup_tax_included },
    } = state;
    let sum = 0;

    for (let i = 0; i < selectedItems.length; i++) {
      if (isTopup(selectedItems[i])) {
        sum += selectedItems[i].price;
      }
    }

    const sumTotalWithoutTax = getPriceWithoutTax(sum, tax_rate.rate);

    return {
      sum: topup_tax_included ? sumTotalWithoutTax : sum,
      tax: topup_tax_included
        ? getTaxPrice(sumTotalWithoutTax, tax_rate.rate)
        : getTaxPrice(sum, tax_rate.rate),
    };
  },
);

export const selectCartCountdown = createSelector(
  [selectSlice],
  state => state.countdown,
);

export const selectCustomerForm = createSelector(
  [selectSlice],
  state => state.selectedEvent.customerform,
);

export const selectHasRegistrationForm = createSelector(
  [selectSlice],
  state => state.selectedEvent.hasRegistrationForm,
);

export const selectWalletCredit = createSelector(
  [selectSlice],
  state => state.selectedEvent.walletcredit?.[0],
);

export const selectBillingInfo = createSelector(
  [selectSlice],
  state => state.billingAndCustomerInfo,
);

export const selectCartEventCurrency = createSelector(
  [selectSlice],
  state => state.selectedEvent.currency,
);

export const selectCartEventHasUnbundledAddonsOrTopUps = createSelector(
  [selectSlice],
  state =>
    state.selectedEvent.walletcredit?.length ||
    state.selectedItems.some(item => {
      if (isTicket(item)) {
        const ticketType = state.selectedEvent.tickets.find(
          ({ id }) => id === item.ticketId,
        );

        // if the ticket type is not found, it means that selectedItems array contains
        // tickets from multiple events. same below for bundles
        if (!ticketType) {
          return false;
        }

        return ticketHasUnbundledAddons(ticketType);
      } else if (isBundle(item)) {
        const bundle = state.selectedEvent.ticketTypeBundles?.find(
          ({ id }) => id === item.id,
        );

        if (!bundle) {
          return false;
        }

        const ticketTypes = getTicketTypes(
          bundle.items,
          state.selectedEvent.tickets,
        );

        for (const ticketType of ticketTypes) {
          if (ticketHasUnbundledAddons(ticketType)) {
            return true;
          }
        }
      }
      return false;
    }),
);
